import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { X, ArrowUpRight } from 'lucide-react';
import { footerLinks } from '../mock';
import { colors } from '../styles/colors';
import { useFillout } from '../hooks/useFillout';

// Recover & reuse the existing introducer form URL (unchanged destination)
const INTRODUCER_URL =
  Object.values(footerLinks)
    .flat()
    .find((l) => l.label === 'Become an Introducer')?.href || '';

const Footer = () => {
  const { openFilloutPopup } = useFillout();
  const [introducerOpen, setIntroducerOpen] = useState(false);

  useEffect(() => {
    if (!introducerOpen) return;
    const onKey = (e) => { if (e.key === 'Escape') setIntroducerOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [introducerOpen]);

  return (
    <footer 
      style={{ backgroundColor: colors.graphite }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-7 gap-8 sm:gap-12 mb-8 sm:mb-12">
          {/* Brand column */}
          <div className="col-span-2 lg:col-span-2">
            <div className="mb-4">
              <img 
                src="/logo-white-footer-new.png" 
                alt="Arizore" 
                className="h-14 sm:h-16 w-auto"
              />
            </div>
            <p 
              className="text-xs sm:text-sm leading-relaxed"
              style={{ color: colors.white, opacity: 0.6 }}
            >
              Building a transparent reserve ecosystem for tokenised natural resources.
            </p>
            {/* Social Icons */}
            <div className="flex items-center gap-4 mt-4">
              {/* LinkedIn Icon */}
              <a
                href="https://linkedin.com/company/arizore"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block transition-opacity duration-200"
                style={{ opacity: 0.6 }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.opacity = 1;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.opacity = 0.6;
                }}
              >
                <img 
                  src="/linkedin-logo-white.png" 
                  alt="LinkedIn" 
                  className="h-12 w-12 sm:h-14 sm:w-14"
                />
              </a>
              {/* Spotify Icon */}
              <a
                href="https://open.spotify.com/show/7qhMnFWrSkCzf4MAFIwgmn?si=e5c37a78c42c40c0"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block transition-opacity duration-200"
                style={{ opacity: 0.6 }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.opacity = 1;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.opacity = 0.6;
                }}
              >
                <img 
                  src="/spotify-logo-white.png" 
                  alt="Spotify" 
                  className="h-8 w-8 sm:h-10 sm:w-10"
                />
              </a>
            </div>
          </div>

          {/* Footer links */}
          {Object.entries(footerLinks).map(([category, links]) => (
            <div key={category}>
              <h4 
                className="font-semibold mb-3 sm:mb-4 text-sm sm:text-base"
                style={{ color: colors.white }}
              >
                {category}
              </h4>
              <ul className="space-y-1.5 sm:space-y-2">
                {links.map((link) => (
                  <li key={link.label}>
                    {link.label === 'Get In Touch' ? (
                      <button
                        onClick={openFilloutPopup}
                        data-testid="footer-contact-btn"
                        className="text-xs sm:text-sm transition-colors duration-200 cursor-pointer text-left"
                        style={{ color: colors.white, opacity: 0.6, background: 'none', border: 'none', padding: 0 }}
                        onMouseEnter={(e) => {
                          e.target.style.color = colors.teal;
                          e.target.style.opacity = 1;
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.color = colors.white;
                          e.target.style.opacity = 0.6;
                        }}
                      >
                        {link.label}
                      </button>
                    ) : link.label === 'Become an Introducer' ? (
                      <button
                        onClick={() => setIntroducerOpen(true)}
                        data-testid="footer-become-introducer"
                        className="text-xs sm:text-sm transition-colors duration-200 cursor-pointer text-left"
                        style={{ color: colors.white, opacity: 0.6, background: 'none', border: 'none', padding: 0 }}
                        onMouseEnter={(e) => {
                          e.target.style.color = colors.teal;
                          e.target.style.opacity = 1;
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.color = colors.white;
                          e.target.style.opacity = 0.6;
                        }}
                      >
                        {link.label}
                      </button>
                    ) : (
                      <a
                        href={link.href}
                        target={link.external ? '_blank' : undefined}
                        rel={link.external ? 'noopener noreferrer' : undefined}
                        className="text-xs sm:text-sm transition-colors duration-200"
                        style={{ color: colors.white, opacity: 0.6 }}
                        onMouseEnter={(e) => {
                          e.target.style.color = colors.teal;
                          e.target.style.opacity = 1;
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.color = colors.white;
                          e.target.style.opacity = 0.6;
                        }}
                      >
                        {link.label}
                      </a>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Bottom bar */}
        <div 
          className="border-t pt-6 sm:pt-8 flex flex-col sm:flex-row justify-between items-center space-y-4 sm:space-y-0 text-center sm:text-left"
          style={{ borderColor: `${colors.white}20` }}
        >
          <p 
            className="text-xs sm:text-sm"
            style={{ color: colors.white, opacity: 0.4 }}
          >
            © 2025 Arizore. All rights reserved.
          </p>
          <div className="flex space-x-4 sm:space-x-6">
            <Link 
              to="/privacy-policy" 
              className="text-xs sm:text-sm transition-colors duration-200"
              style={{ color: colors.white, opacity: 0.4 }}
              onMouseEnter={(e) => {
                e.target.style.color = colors.teal;
                e.target.style.opacity = 1;
              }}
              onMouseLeave={(e) => {
                e.target.style.color = colors.white;
                e.target.style.opacity = 0.4;
              }}
            >
              Privacy Policy
            </Link>
            <Link 
              to="/terms-of-service" 
              className="text-xs sm:text-sm transition-colors duration-200"
              style={{ color: colors.white, opacity: 0.4 }}
              onMouseEnter={(e) => {
                e.target.style.color = colors.teal;
                e.target.style.opacity = 1;
              }}
              onMouseLeave={(e) => {
                e.target.style.color = colors.white;
                e.target.style.opacity = 0.4;
              }}
            >
              Terms of Service
            </Link>
          </div>
        </div>
      </div>

      {introducerOpen && (
        <div
          data-testid="introducer-modal"
          className="fixed inset-0 z-[100] flex items-center justify-center p-4"
          onClick={() => setIntroducerOpen(false)}
        >
          <div className="absolute inset-0" style={{ backgroundColor: 'rgba(0,0,0,0.78)', backdropFilter: 'blur(6px)' }} />
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="introducer-modal-title"
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-md rounded-2xl p-7 sm:p-8"
            style={{ background: 'linear-gradient(180deg, #0B0F12 0%, #07090B 100%)', border: `1px solid ${colors.teal}33`, boxShadow: '0 40px 90px -30px rgba(0,0,0,0.85)' }}
          >
            <button
              type="button"
              onClick={() => setIntroducerOpen(false)}
              data-testid="introducer-modal-close-x"
              aria-label="Close"
              className="absolute top-4 right-4 w-8 h-8 rounded-full flex items-center justify-center transition-colors duration-200"
              style={{ color: colors.white, background: 'rgba(255,255,255,0.06)' }}
              onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.14)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.06)'; }}
            >
              <X className="w-4 h-4" />
            </button>

            <div className="flex items-center gap-3 mb-4">
              <span className="h-px w-8" style={{ backgroundColor: colors.teal }} />
              <span className="text-[11px] uppercase tracking-[0.28em] font-medium" style={{ color: colors.teal }}>Introducer Pathway</span>
            </div>
            <h3 id="introducer-modal-title" className="text-2xl font-bold mb-3 tracking-tight" style={{ color: colors.white }}>
              Become an Introducer
            </h3>
            <p className="text-sm sm:text-base leading-relaxed mb-7" style={{ color: colors.white, opacity: 0.7 }}>
              Apply to participate in the Arizore introducer pathway. Approved introducers receive access to approved outreach guidance, supporting materials, and ecosystem information.
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <a
                href={INTRODUCER_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="introducer-modal-open-application"
                onClick={() => setIntroducerOpen(false)}
                className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-lg text-sm font-semibold uppercase tracking-[0.14em] transition-all duration-300 flex-1"
                style={{ backgroundColor: colors.teal, color: colors.white, textDecoration: 'none' }}
                onMouseEnter={(e) => { e.currentTarget.style.boxShadow = `0 0 0 4px ${colors.teal}26`; e.currentTarget.style.transform = 'translateY(-1px)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.boxShadow = 'none'; e.currentTarget.style.transform = 'translateY(0)'; }}
              >
                Open Introducer Application
                <ArrowUpRight className="w-4 h-4" />
              </a>
              <button
                type="button"
                onClick={() => setIntroducerOpen(false)}
                data-testid="introducer-modal-close-btn"
                className="inline-flex items-center justify-center px-6 py-3.5 rounded-lg text-sm font-semibold uppercase tracking-[0.14em] transition-all duration-300"
                style={{ backgroundColor: 'transparent', color: colors.white, border: '1px solid rgba(255,255,255,0.25)' }}
                onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.06)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.45)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.25)'; }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </footer>
  );
};

export default Footer;